import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api, clearTokens } from '../lib/api-client';
import type { PublicUser } from '@flymind/shared';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useOneSignal } from '../hooks/useOneSignal';
import { useToast } from '../components/ui/Toast';
import { useTranslation } from '../hooks/useTranslation';

type SettingsTab = 'profilo' | 'invita' | 'notifiche' | 'privacy' | 'info';

// --- Toggle component ---
function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex items-center justify-between gap-4 cursor-pointer py-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-text-primary">{label}</div>
        {description && (
          <div className="text-xs text-text-secondary mt-0.5">{description}</div>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 cursor-pointer ${
          checked ? 'bg-primary-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white transition-transform shadow-sm ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </label>
  );
}

// --- Language Selector ---
function LanguageSelector() {
  const { user, refreshUser } = useAuth();
  const { showToast } = useToast();
  const { t, locale } = useTranslation();
  const [saving, setSaving] = useState(false);

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLocale = e.target.value;
    if (newLocale === locale) return;
    setSaving(true);
    try {
      await api.patch<PublicUser>('/users/me', { locale: newLocale });
      await refreshUser();
      showToast(t('settings.languageUpdated'), 'success');
    } catch {
      showToast(t('common.error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-text-primary">{t('settings.language')}</label>
      <select
        value={user?.locale || locale}
        onChange={handleChange}
        disabled={saving}
        className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <option value="it">Italiano</option>
        <option value="en">English</option>
      </select>
    </div>
  );
}

// --- Profile Section ---
function ProfiloSection() {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      await api.patch<PublicUser>('/users/me', { name: name.trim() || null });
      await refreshUser();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-text-primary mb-4">{t('settings.profile')}</h3>
      </div>

      <Input
        label={t('settings.nameLabel')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('settings.namePlaceholder')}
      />

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-text-primary">{t('settings.emailLabel')}</label>
        <div className="flex items-center gap-2">
          <div className="flex-1 rounded-lg border border-border bg-surface-secondary px-3 py-2 text-sm text-text-secondary">
            {user?.email}
          </div>
          {user?.emailVerified && (
            <Badge variant="good" size="sm">
              {t('settings.verified')}
            </Badge>
          )}
          {user && !user.emailVerified && (
            <Badge variant="rare" size="sm">
              {t('settings.notVerified')}
            </Badge>
          )}
        </div>
      </div>

      <LanguageSelector />

      {error && (
        <p className="text-sm text-alert-exceptional">{error}</p>
      )}
      {success && (
        <p className="text-sm text-green-600">{t('settings.saveChangesSuccess')}</p>
      )}

      <Button onClick={handleSave} isLoading={saving}>
        {t('settings.saveChanges')}
      </Button>
    </div>
  );
}

// --- Notifications Section ---
function NotificheSection() {
  const { t } = useTranslation();
  const [emailNotif, setEmailNotif] = useState(true);
  const [inAppNotif, setInAppNotif] = useState(true);
  const [minLevel, setMinLevel] = useState<string>('buono');
  const { isSubscribed, isSupported, requestPermission, syncToBackend, unsubscribe } = useOneSignal();
  const [pushLoading, setPushLoading] = useState(false);

  const handlePushToggle = async (enabled: boolean) => {
    setPushLoading(true);
    try {
      if (enabled) {
        await requestPermission();
        // Wait for OneSignal to assign a player ID after opt-in
        await new Promise((resolve) => setTimeout(resolve, 2000));
        // Re-read playerId from OneSignal directly (state may be stale)
        const OS = (window as any).OneSignal;
        const newId = OS?.User?.PushSubscription?.id;
        if (newId) {
          await syncToBackend(newId);
        }
      } else {
        await unsubscribe();
      }
    } catch (err) {
      console.error('Push toggle error:', err);
    } finally {
      setPushLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <h3 className="text-base font-semibold text-text-primary mb-4">{t('settings.notifications')}</h3>

      <div className="divide-y divide-border">
          <div className="flex items-center justify-between gap-4 py-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-text-primary flex items-center gap-2">
                {t('notifications.pushNotifications')}
                {isSubscribed ? (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                    {t('settings.pushOn')}
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                    {t('settings.pushOff')}
                  </span>
                )}
              </div>
              <div className="text-xs text-text-secondary mt-0.5">
                {isSupported
                  ? t('settings.pushDescription')
                  : t('settings.pushUnsupported')}
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isSubscribed}
              disabled={pushLoading || !isSupported}
              onClick={() => handlePushToggle(!isSubscribed)}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 cursor-pointer disabled:opacity-50 ${
                isSubscribed ? 'bg-primary-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-white transition-transform shadow-sm ${
                  isSubscribed ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        <Toggle
          checked={emailNotif}
          onChange={setEmailNotif}
          label={t('notifications.emailNotifications')}
          description={t('settings.emailDescription')}
        />
        <Toggle
          checked={inAppNotif}
          onChange={setInAppNotif}
          label={t('notifications.inAppNotifications')}
          description={t('settings.inAppDescription')}
        />
      </div>

      <div className="flex flex-col gap-1.5 pt-3">
        <label className="text-sm font-medium text-text-primary">
          {t('settings.minAlertLevel')}
        </label>
        <select
          value={minLevel}
          onChange={(e) => setMinLevel(e.target.value)}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="buono">{t('settings.levelGood')}</option>
          <option value="raro">{t('settings.levelRare')}</option>
          <option value="eccezionale">{t('settings.levelExceptional')}</option>
        </select>
        <p className="text-xs text-text-secondary">
          {t('settings.levelHelp')}
        </p>
      </div>

      <p className="text-xs text-text-secondary pt-4 italic">
        {t('settings.notifPrefsSavedSoon')}
      </p>
    </div>
  );
}

// --- Privacy Section ---
function PrivacySection() {
  const { t } = useTranslation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExportData = async () => {
    setExporting(true);
    try {
      // Placeholder: GDPR export endpoint
      await new Promise((r) => setTimeout(r, 1000));
      alert(t('settings.exportComingSoon'));
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete('/users/me');
      // Clear tokens manually (avoid useAuth logout() which triggers ProtectedRoute
      // redirect to /login before we can show the confirmation page).
      clearTokens();
      // Full reload ensures all in-memory auth state is wiped and the
      // AuthProvider re-initialises as unauthenticated on the public page.
      window.location.href = '/account-deleted?success=true';
    } catch (err) {
      alert(err instanceof Error ? err.message : t('settings.deleteError'));
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-5">
      <h3 className="text-base font-semibold text-text-primary mb-4">
        {t('settings.privacyAndData')}
      </h3>

      <Card variant="default" padding="md">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-text-primary">
              {t('settings.exportMyData')}
            </div>
            <div className="text-xs text-text-secondary mt-0.5">
              {t('settings.exportMyDataDescription')}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportData}
            isLoading={exporting}
          >
            {t('settings.exportButton')}
          </Button>
        </div>
      </Card>

      <Card variant="default" padding="md" className="border-red-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-alert-exceptional">
              {t('settings.deleteAccountTitle')}
            </div>
            <div className="text-xs text-text-secondary mt-0.5">
              {t('settings.deleteAccountDescription')}
            </div>
          </div>
          <Button
            variant="danger"
            size="sm"
            onClick={() => setShowDeleteDialog(true)}
          >
            {t('settings.deleteAccountTitle')}
          </Button>
        </div>
      </Card>

      <ConfirmDialog
        open={showDeleteDialog}
        title={t('settings.deleteDialogTitle')}
        message={t('settings.deleteDialogMessage')}
        typedConfirmation={t('settings.deleteTypedConfirm')}
        confirmLabel={t('settings.deleteAccountTitle')}
        cancelLabel={t('common.cancel')}
        variant="danger"
        isLoading={deleting}
        onConfirm={handleDeleteAccount}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </div>
  );
}

// --- Info Section ---
function InfoSection() {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <h3 className="text-base font-semibold text-text-primary mb-4">{t('settings.info')}</h3>

      <div className="space-y-3">
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-text-secondary">{t('settings.appVersion')}</span>
          <span className="text-sm font-medium text-text-primary">0.1.0</span>
        </div>

        <div className="border-t border-border" />

        <a
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between py-2 text-sm text-primary-600 hover:text-primary-700 transition-colors"
        >
          <span>{t('settings.privacyPolicy')}</span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <path fillRule="evenodd" d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z" clipRule="evenodd" />
          </svg>
        </a>

        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between py-2 text-sm text-primary-600 hover:text-primary-700 transition-colors"
        >
          <span>{t('settings.termsOfService')}</span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <path fillRule="evenodd" d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z" clipRule="evenodd" />
          </svg>
        </a>

        <a
          href="mailto:supporto@flymind.app"
          className="flex items-center justify-between py-2 text-sm text-primary-600 hover:text-primary-700 transition-colors"
        >
          <span>{t('settings.support')}</span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <path d="M3 4a2 2 0 00-2 2v1.161l8.441 4.221a1.25 1.25 0 001.118 0L19 7.162V6a2 2 0 00-2-2H3z" />
            <path d="M19 8.839l-7.77 3.885a2.75 2.75 0 01-2.46 0L1 8.839V14a2 2 0 002 2h14a2 2 0 002-2V8.839z" />
          </svg>
        </a>
      </div>
    </div>
  );
}

// --- Invite Section ---
interface InviteMyCodeResponse {
  code: string;
  inviteUrl: string;
  stats: {
    totalInvites: number;
    nextMilestone: number;
    monthsEarned: number;
    premiumUntil: string | null;
  };
}

interface InviteStatsResponse {
  totalInvites: number;
  monthsEarned: number;
  premiumUntil: string | null;
  invitees: { name: string | null; createdAt: string }[];
}

function InviteSection() {
  const { t, locale } = useTranslation();
  const { showToast } = useToast();
  const [myCode, setMyCode] = useState<InviteMyCodeResponse | null>(null);
  const [stats, setStats] = useState<InviteStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [codeData, statsData] = await Promise.all([
          api.get<InviteMyCodeResponse>('/invite/my-code'),
          api.get<InviteStatsResponse>('/invite/stats'),
        ]);
        setMyCode(codeData);
        setStats(statsData);
      } catch (err) {
        console.error('Failed to load invite data:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  const inviteUrl = myCode?.inviteUrl || '';
  const totalInvites = myCode?.stats.totalInvites ?? 0;
  const monthsEarned = myCode?.stats.monthsEarned ?? 0;
  const premiumUntil = myCode?.stats.premiumUntil;
  const progress = totalInvites % 5;

  const shareText = `${t('settings.shareTextTemplate')} ${inviteUrl}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      showToast(t('settings.linkCopiedToast'), 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast(t('settings.copyErrorToast'), 'error');
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: 'FlyMind',
        text: shareText,
        url: inviteUrl,
      });
    } catch {
      // User cancelled or not supported
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-text-primary mb-1">
          {t('settings.inviteTitle')}
        </h3>
        <p className="text-sm text-text-secondary">
          {t('settings.inviteSubtitle')}
        </p>
      </div>

      {/* Invite link */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-text-primary">
          {t('settings.yourInviteLink')}
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={inviteUrl}
            className="flex-1 rounded-lg border border-border bg-surface-secondary px-3 py-2 text-sm text-text-secondary truncate"
          />
          <Button variant="outline" size="sm" onClick={handleCopy}>
            {copied ? t('settings.copied') : t('settings.copy')}
          </Button>
        </div>
      </div>

      {/* Social sharing */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-text-primary">
          {t('settings.share')}
        </label>
        <div className="flex items-center gap-3">
          {/* WhatsApp */}
          <a
            href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-10 w-10 items-center justify-center rounded-full transition-opacity hover:opacity-80"
            style={{ backgroundColor: '#25D366' }}
            title="WhatsApp"
          >
            <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </a>

          {/* Telegram */}
          <a
            href={`https://t.me/share/url?url=${encodeURIComponent(inviteUrl)}&text=${encodeURIComponent(shareText)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-10 w-10 items-center justify-center rounded-full transition-opacity hover:opacity-80"
            style={{ backgroundColor: '#0088cc' }}
            title="Telegram"
          >
            <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
            </svg>
          </a>

          {/* Facebook */}
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(inviteUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-10 w-10 items-center justify-center rounded-full transition-opacity hover:opacity-80"
            style={{ backgroundColor: '#1877F2' }}
            title="Facebook"
          >
            <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
          </a>

          {/* X/Twitter */}
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(inviteUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-black transition-opacity hover:opacity-80"
            title="X"
          >
            <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>

          {/* Native share */}
          {typeof navigator !== 'undefined' && 'share' in navigator && (
            <button
              onClick={handleNativeShare}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-400 transition-opacity hover:opacity-80 cursor-pointer"
              title={t('settings.shareAria')}
            >
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-text-primary">
            {`${totalInvites}/5 ${t('settings.progressLabel')}`}
          </span>
          <span className="text-sm font-semibold text-primary-600">
            {progress}/5
          </span>
        </div>
        <div className="h-3 w-full rounded-full bg-surface-secondary overflow-hidden">
          <div
            className="h-full rounded-full bg-primary-600 transition-all duration-500"
            style={{ width: `${(progress / 5) * 100}%` }}
          />
        </div>
        {monthsEarned > 0 && (
          <p className="text-sm text-green-600 font-medium">
            {`${t('settings.monthsEarnedPrefix')} ${monthsEarned} ${t('settings.monthsEarnedSuffix')}`}
          </p>
        )}
        {premiumUntil && (
          <p className="text-sm text-text-secondary">
            {t('settings.proActiveUntil')}{' '}
            <span className="font-medium text-text-primary">
              {new Date(premiumUntil).toLocaleDateString(locale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </span>
          </p>
        )}
      </div>

      {/* Invited friends list */}
      {stats && stats.invitees.length > 0 && (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-text-primary">
            {t('settings.invitedFriends')}
          </label>
          <Card variant="default" padding="none">
            <ul className="divide-y divide-border">
              {stats.invitees.map((inv, i) => (
                <li key={i} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary-700">
                        {(inv.name || '?')[0].toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm text-text-primary">
                      {inv.name || t('settings.fallbackUserLabel')}
                    </span>
                  </div>
                  <span className="text-xs text-text-secondary">
                    {new Date(inv.createdAt).toLocaleDateString(locale, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      {stats && stats.invitees.length === 0 && (
        <p className="text-sm text-text-secondary italic">
          {t('settings.noInvitesYet')}
        </p>
      )}
    </div>
  );
}

// --- Main Settings page ---
export default function Settings() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as SettingsTab) || 'profilo';

  const tabs: { id: SettingsTab; label: string }[] = [
    { id: 'profilo', label: t('settings.tabProfile') },
    { id: 'invita', label: t('settings.tabInvite') },
    { id: 'notifiche', label: t('settings.tabNotifications') },
    { id: 'privacy', label: t('settings.tabPrivacy') },
    { id: 'info', label: t('settings.tabInfo') },
  ];

  const [activeTab, setActiveTab] = useState<SettingsTab>(
    tabs.some((tab) => tab.id === initialTab) ? initialTab : 'profilo',
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 sm:py-8">
      <h1 className="text-xl font-bold text-text-primary mb-6">{t('settings.title')}</h1>

      {/* Tab navigation - horizontal scroll on mobile */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
              activeTab === tab.id
                ? 'bg-primary-100 text-primary-700'
                : 'text-text-secondary hover:bg-surface-secondary hover:text-text-primary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <Card variant="default" padding="lg">
        {activeTab === 'profilo' && <ProfiloSection />}
        {activeTab === 'invita' && <InviteSection />}
        {activeTab === 'notifiche' && <NotificheSection />}
        {activeTab === 'privacy' && <PrivacySection />}
        {activeTab === 'info' && <InfoSection />}
      </Card>
    </div>
  );
}
